import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PhotoSwipeEventName, usePhotoswipe } from 'src/hooks/usePhotoswipe/usePhotoswipe';
import { MediaEntry } from 'src/types/global-types';

/**
 * Thumbnail deferral is native `loading="lazy"`, via `LazyImg` in
 * `ProductGallery.tsx`.
 *
 * This hook used to carry an `observeLazyThumbs` IntersectionObserver that swapped
 * `data-src` onto `src`. It never ran: the `thumbsRef` it keyed off was returned by
 * the hook but never attached to an element, so `thumbsRef.current` was always
 * undefined and the effect bailed on its first line. The thumbnails it was meant to
 * defer were also rendered with `src` AND `data-src` set to the same URL, so even
 * had it run, every thumbnail had already been fetched by the time it observed
 * anything. Removed in favour of the attribute the rest of the codebase uses.
 */
export function useProductGallery(media_entries: MediaEntry[]) {
    const hasThumbs = media_entries.length > 1;
    const mainRef = useRef<HTMLImageElement | undefined>(undefined);

    const [selectedIndex, setSelected] = useState(0);

    /**
     * Whether the main image is mid-download. Drives `.gallerySpinner`, which was
     * previously bound only to the provider's `pending` flag — and `pending` is set
     * solely on a location-search change (a variant swap), never on first paint or
     * on a thumbnail click. Since `.galleryImage` stays at `opacity: 0` until
     * `data-loaded` lands, that left the viewport simply blank while an image
     * downloaded, with no indication anything was happening.
     *
     * Starts `false` so SSR markup carries no spinner: the effect below doesn't run
     * on the server, so a `true` default would ship a spinner that never clears if
     * the client fails to hydrate.
     */
    const [mainImageLoading, setMainImageLoading] = useState(false);
    const isFirstImage = useRef(true);

    useEffect(() => {
        setSelected(0);
    }, [media_entries]);

    useEffect(() => {
        /**
         * `mainRef` is unset while a video is selected (an `<iframe>` renders in place
         * of the `<img>`), so clear the flag rather than leaving a spinner running over
         * the video from an image that was still loading when the user switched.
         */
        if (!mainRef.current) {
            setMainImageLoading(false);
            return undefined;
        }
        const current = mainRef.current;
        /**
         * Main image transitions. React owns `src` via the JSX now; this
         * effect's only job is to drive the CSS opacity transition.
         *
         * `.galleryImage { opacity: 0 }` + `&[data-loaded] { opacity: 1 }` is
         * the contract: nothing is visible until `data-loaded` is set. The
         * previous implementation cleared `current.src = ''` without ever
         * restoring it from `data-src` AND never set `data-loaded`, so the
         * main product image was always invisible (lozad used to set
         * `data-loaded` but was removed in WWE-2655; see useLazyLoad.tsx).
         */
        const isInitial = isFirstImage.current;
        isFirstImage.current = false;
        if (!isInitial) {
            current.classList.add('fade-out');
            current.removeAttribute('data-loaded');
        }
        const onLoad = () => {
            current.setAttribute('data-loaded', 'true');
            current.classList.remove('fade-out');
            setMainImageLoading(false);
        };
        /**
         * A broken or 404 image never fires `load`. Without this the spinner would
         * keep turning over an empty viewport indefinitely.
         */
        const onError = () => setMainImageLoading(false);
        current.addEventListener('load', onLoad, { once: true });
        current.addEventListener('error', onError, { once: true });
        if (current.complete) {
            // Already settled: either cached (no `load` to come) or already failed.
            if (current.naturalWidth > 0) {
                onLoad();
            } else {
                setMainImageLoading(false);
            }
        } else if (!isInitial) {
            setMainImageLoading(true);
        }
        return () => {
            current.removeEventListener('load', onLoad);
            current.removeEventListener('error', onError);
        };
    }, [media_entries, selectedIndex]);

    const sortedMediaEntries = useMemo(() => {
        return media_entries.slice().sort((a, b) => a.position - b.position);
    }, [media_entries]);

    const selected = sortedMediaEntries[selectedIndex];

    return {
        hasThumbs,
        selected,
        mainRef,
        mainImageLoading,
        sortedMediaEntries,
        selectedIndex,
        setSelected,
    };
}

interface PhotoSwipeParams {
    selectedIndex: number;
    sortedMediaEntries: MediaEntry[];
}

export function usePhotoswipeForGallery(params: PhotoSwipeParams) {
    const { selectedIndex, sortedMediaEntries } = params;
    const photoSwipeRef = useRef<HTMLDivElement | null>(null);
    const [msg, dispatch] = usePhotoswipe(photoSwipeRef as any);

    const showPhotoswipe = useCallback(() => {
        dispatch({
            index: selectedIndex,
            images: sortedMediaEntries.map((img) => {
                return {
                    h: img.large_height,
                    w: img.large_width,
                    src: img.large_url,
                    title: undefined,
                };
            }),
        });
    }, [dispatch, selectedIndex, sortedMediaEntries]);

    return {
        showPhotoswipe,
        photoswipeReady: msg.type === PhotoSwipeEventName.Ready,
        photoswipeHtml: (msg.type === PhotoSwipeEventName.Ready && msg.html) || '',
        photoSwipeRef,
    };
}
